import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCardIcon,
  PhoneIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

type InvoiceRow = {
  id: number;
  number: string;
  createdDate: string;
  dueDate: string;
  amountToPay: number;
  currencyCode: string;
  status: number;
  payUrl: string | null;
};

type Step = 'phone' | 'code' | 'invoices';

const DEFAULT_SESSION_TTL = 300;

const formatMoney = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);

const formatDate = (value: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRemaining = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const beaconLogout = () => {
  try {
    const fd = new FormData();
    fd.append('action', 'logout');
    navigator.sendBeacon('/api/pay-bill.php', fd);
  } catch {
    /* ignore */
  }
};

const PayBill: React.FC = () => {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [clientName, setClientName] = useState('');
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [totalDue, setTotalDue] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const verifiedRef = useRef(false);

  const clearLocalSession = useCallback((expiredMessage?: string) => {
    verifiedRef.current = false;
    setExpiresAt(null);
    setRemainingSec(0);
    setStep('phone');
    setPhone('');
    setCode('');
    setInvoices([]);
    setTotalDue(0);
    setClientName('');
    setPhoneHint('');
    setMessage('');
    setError(expiredMessage || '');
  }, []);

  const armSession = useCallback((expiresIn?: number, sessionTtl?: number) => {
    const ttl = Math.max(
      1,
      Number(expiresIn) > 0 ? Number(expiresIn) : Number(sessionTtl) || DEFAULT_SESSION_TTL
    );
    verifiedRef.current = true;
    setExpiresAt(Date.now() + ttl * 1000);
    setRemainingSec(ttl);
  }, []);

  const startOver = useCallback(
    async (expiredMessage?: string) => {
      try {
        await fetch('/api/pay-bill.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ action: 'logout' }),
        });
      } catch {
        /* ignore */
      }
      clearLocalSession(expiredMessage);
    },
    [clearLocalSession]
  );

  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/pay-bill.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'invoices' }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.locked) {
          await startOver('Session expired. Enter your phone again.');
          return;
        }
        throw new Error(data.error || 'Could not load invoices');
      }
      setClientName(data.clientName || '');
      setPhoneHint(data.phoneHint || '');
      setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
      setTotalDue(Number(data.totalDue) || 0);
      armSession(data.expiresIn, data.sessionTtl);
      setStep('invoices');
      window.AirmaxVisitor?.identify({
        clientName: data.clientName || '',
        clientPhoneHint: data.phoneHint || '',
        clientContext: 'pay-bill',
        supportAction: 'Viewing invoices',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Do not restore a previous session on load — always require a fresh login.
  // Also clear any leftover server session from a prior visit.
  useEffect(() => {
    void (async () => {
      try {
        await fetch('/api/pay-bill.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ action: 'logout' }),
        });
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Sign out when the tab is closed or refreshed
  useEffect(() => {
    const onLeave = () => {
      if (verifiedRef.current) {
        beaconLogout();
      }
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
    };
  }, []);

  // Live countdown — auto sign-out at 0
  useEffect(() => {
    if (!expiresAt || step !== 'invoices') return undefined;
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemainingSec(left);
      if (left <= 0) {
        void startOver('Signed out for security. Verify your phone again.');
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, step, startOver]);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await fetch('/api/pay-bill.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'send_code', phone }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not send code');
      }
      setPhoneHint(data.phoneHint || '');
      setMessage(data.message || 'Code sent');
      setStep('code');
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/pay-bill.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'verify_code', code }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not verify code');
      }
      setClientName(data.clientName || '');
      setPhoneHint(data.phoneHint || '');
      armSession(data.expiresIn, data.sessionTtl);
      window.AirmaxVisitor?.identify({
        clientName: data.clientName || '',
        clientPhoneHint: data.phoneHint || '',
        clientContext: 'pay-bill',
        supportAction: 'Verified phone',
      });
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify code');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="relative bg-gradient-to-br from-airmax-blue to-blue-700 overflow-hidden">
        <div className="py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <CreditCardIcon className="h-12 w-12 text-white/90 mx-auto mb-4" />
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">Pay Your Bill</h1>
              <p className="text-lg text-blue-100 max-w-xl mx-auto">
                Enter the phone number on your Airmax account. We’ll text a code, then show your
                invoice pay links — same as the email.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
            {step === 'phone' && (
              <form onSubmit={sendCode} className="space-y-5">
                <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <LockClosedIcon className="h-5 w-5 text-airmax-blue flex-shrink-0 mt-0.5" />
                  <p>
                    We verify it’s you with a one-time text. For security, you’ll be signed out after
                    a few minutes or if you leave this page.
                  </p>
                </div>
                <div>
                  <label htmlFor="pay-phone" className="block text-sm font-semibold text-gray-800 mb-2">
                    Account phone number
                  </label>
                  <div className="relative">
                    <PhoneIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      id="pay-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(928) 555-1234"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-airmax-blue focus:border-airmax-blue"
                      required
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-airmax-blue text-white font-semibold py-3 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Sending…' : 'Text me a code'}
                </button>
              </form>
            )}

            {step === 'code' && (
              <form onSubmit={verifyCode} className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Enter your code</h2>
                  <p className="text-sm text-gray-600">
                    {message || `We sent a 6-digit code to ${phoneHint || 'your phone'}.`}
                  </p>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-widest font-semibold focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  required
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full rounded-xl bg-airmax-blue text-white font-semibold py-3 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Checking…' : 'Verify & show invoices'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setError('');
                    setMessage('');
                  }}
                  className="w-full text-sm font-medium text-airmax-blue"
                >
                  Use a different number
                </button>
              </form>
            )}

            {step === 'invoices' && (
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-bold text-gray-900">
                      {clientName ? `Hi, ${clientName.split(' ')[0]}` : 'Your invoices'}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Verified {phoneHint}. Tap Pay to open the same secure payment page from your
                      email.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
                  <ClockIcon className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Auto sign-out in <strong>{formatRemaining(remainingSec)}</strong>
                  </span>
                </div>

                {loading && <p className="text-sm text-gray-500">Loading invoices…</p>}
                {error && <p className="text-sm text-red-600">{error}</p>}

                {!loading && invoices.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
                    <p className="font-medium text-gray-800">No unpaid invoices</p>
                    <p className="text-sm text-gray-500 mt-1">You’re all caught up.</p>
                  </div>
                )}

                {invoices.length > 0 && (
                  <>
                    <p className="text-sm font-semibold text-gray-700">
                      Total due: {formatMoney(totalDue)}
                    </p>
                    <ul className="space-y-3">
                      {invoices.map((inv) => (
                        <li
                          key={inv.id}
                          className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">
                              Invoice #{inv.number || inv.id}
                            </p>
                            <p className="text-sm text-gray-600">
                              Due {formatDate(inv.dueDate)} ·{' '}
                              {formatMoney(inv.amountToPay, inv.currencyCode)}
                            </p>
                          </div>
                          {inv.payUrl ? (
                            <a
                              href={inv.payUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-airmax-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Pay now
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">Payment link unavailable</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => void loadInvoices()}
                    disabled={loading}
                    className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Refresh invoices
                  </button>
                  <button
                    type="button"
                    onClick={() => void startOver()}
                    className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            Need help?{' '}
            <a href="tel:928-565-6707" className="text-airmax-blue font-semibold">
              928-565-6707
            </a>
            {' · '}
            <Link to="/support" className="text-airmax-blue font-semibold">
              Support
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PayBill;
